<script setup lang="ts">
import { NounDeclension } from '~/src/classes.ts';
import { loadNounConventions } from '~/src/data.ts';
import type { NounClass, NounConvention } from '~/src/nouns.ts';

const props = defineProps<{
    nounClass: NounClass & { key: string };
    nounConvention: NounConvention;
    plural?: boolean;
}>();

const nounConventions = await loadNounConventions();

if (nounConventions === undefined) {
    throw new Error('no nounConventions defined');
}

const template = computed(() => props.nounConvention.templates[props.nounClass.key]);

const stem = computed(() => {
    if (!template.value) {
        return undefined;
    }
    return props.nounClass.exampleStems[template.value.stem ?? 'default'];
});

const numerus = computed(() => {
    return !props.plural ? 'singular' : 'plural';
});

const declension = computed(() => {
    if (!template.value || !stem.value) {
        return undefined;
    }

    const declension = nounConventions.declensions[template.value.declension];
    if (!declension[numerus.value]) {
        return undefined;
    }

    const entries = Object.entries(declension[numerus.value] ?? {}).map(([caseAbbreviation, declensionSuffix]) => {
        return [caseAbbreviation.toUpperCase() + (props.plural ? '_pl' : ''),
            nounConventions.classExample[numerus.value][caseAbbreviation].replace(/\{([^}]+)}/, (_match, morpheme) => {
                const value = props.nounConvention.morphemes[morpheme];
                if (value === undefined) {
                    return '';
                }
                return typeof value === 'string' ? value : value.spelling;
            }) + stem.value + template.value.suffix + declensionSuffix];
    });

    return new NounDeclension(Object.fromEntries(entries));
});
</script>

<template>
    <div v-if="declension" class="mb-3">
        <h5 class="h6">
            <template v-if="!plural">
                ⋅ <T>nouns.singular</T>
            </template>
            <template v-else>
                ⁖ <T>nouns.plural</T>
            </template>
        </h5>
        <NounsDeclension word="" :template="declension" open :plural />
    </div>
</template>
